import type { DateRange, Filter } from "@cubejs-client/core";
import { leafMemberOf } from "@hightouch/lib/ad-studio/insights-malloy/schemas/cube-query";
import {
  daysOf,
  formatDateRangeLabel,
  insightsDateRangeSchema,
} from "src/pages/ad-studio/insights/shared/dates";
import { err, ok } from "@hightouch/lib/util/result";
import pluralize from "pluralize";
import { z } from "zod/v4";
import type {
  DraftGroup,
  DraftLeaf,
  DraftRoot,
  FieldOperand,
  FilterBarDraft,
  FilterFolder,
  CompiledGroup,
  CheckGroup,
  OperandSpec,
  OperatorChoiceOption,
  OperatorVocabulary,
  PillSummary,
} from "src/pages/ad-studio/filters/types";
import {
  childrenOf,
  emptyConditionOf,
  isDraftGroup,
  leafSummary,
  logicOf,
  operandSpecOf,
  operatorChoiceOptions,
  checkedAdvancedOf,
  relativeDaysOf,
  rootOf,
  toCheckGroup,
  trimmedLeaf,
} from "src/pages/ad-studio/filters/utils";
import type { AdvancedRejection } from "src/pages/ad-studio/insights/performance/filters/types";
import type {
  ChannelScope,
  Filterable,
  FilterableFolder,
  FilterableMember,
  Folder,
  Found,
  Member,
  PossibleValues,
  ViewMeta,
  ValuesByMember,
  WiredBlend,
} from "src/pages/ad-studio/insights/performance/types";
import {
  syncFilters,
  valuesBlend,
} from "src/pages/ad-studio/insights/performance/utils/query";

const NOT_FOUND = { kind: "not-found" } as const;
const found = <T>(value: T): Found<T> => ({ kind: "found", value });

export const filterableNamed = ({
  filterable,
  name,
}: {
  filterable: Filterable;
  name: string;
}): Found<FilterableMember> => {
  switch (filterable.status) {
    case "loading":
    case "failed": {
      return NOT_FOUND;
    }
    case "loaded": {
      const named = filterable.folders
        .flatMap((folder) => [...folder.members])
        .find(({ member }) => member.name === name);
      return named === undefined ? NOT_FOUND : found(named);
    }
    default: {
      return filterable satisfies never;
    }
  }
};

const queriedNamesOf = (member: Member): string[] => {
  switch (member.kind) {
    case "creative-tag":
    case "queried": {
      return [member.name];
    }
    case "measure":
    case "listed":
    case "free-text":
    case "time":
    case "video-length": {
      return [];
    }
    default: {
      return member satisfies never;
    }
  }
};

export const valuesBlendsOf = ({
  view,
  folders,
  scope,
  dates,
}: {
  view: ViewMeta;
  folders: readonly Folder[];
  scope: readonly ChannelScope[];
  dates: DateRange;
}): readonly WiredBlend<ValuesByMember>[] => {
  const [first, ...rest] = [
    ...new Set(
      folders.flatMap((folder) => folder.members).flatMap(queriedNamesOf),
    ),
  ].sort();
  return first === undefined
    ? []
    : [
        {
          blend: valuesBlend({
            view,
            members: [first, ...rest],
            within: syncFilters({ view, scope }),
            dates,
          }),
          wire: view.wire,
        },
      ];
};

const enumOf = (values: readonly string[]): PossibleValues[] => {
  const [first, ...rest] = values;
  return first === undefined
    ? []
    : [
        {
          kind: "enum",
          values: [
            { value: first, label: first },
            ...rest.map((value) => ({ value, label: value })),
          ],
        },
      ];
};

const possibleValuesOf = ({
  member,
  valuesByMember,
}: {
  member: Member;
  valuesByMember: ReadonlyMap<string, readonly string[]>;
}): PossibleValues[] => {
  switch (member.kind) {
    case "measure": {
      return [{ kind: "number", format: member.format }];
    }
    case "time": {
      return [{ kind: "date" }];
    }
    case "listed": {
      return [{ kind: "enum", values: member.values }];
    }
    case "free-text": {
      return [{ kind: "free-text" }];
    }
    case "video-length": {
      return [{ kind: "number", format: "seconds" }];
    }
    case "creative-tag":
    case "queried": {
      return enumOf(valuesByMember.get(member.name) ?? []);
    }
    default: {
      return member satisfies never;
    }
  }
};

export const toFilterable = ({
  folders,
  valuesByMember,
}: {
  folders: readonly Folder[];
  valuesByMember: ReadonlyMap<string, readonly string[]>;
}): readonly FilterableFolder[] =>
  folders.flatMap((folder) => {
    const [first, ...rest] = folder.members.flatMap((member) =>
      possibleValuesOf({ member, valuesByMember }).map(
        (possibleValues): FilterableMember => ({ member, possibleValues }),
      ),
    );
    return first === undefined
      ? []
      : [{ name: folder.name, members: [first, ...rest] }];
  });

const lastDaysOf = (count: number) => `last ${count} days`;

const daysSchema = z
  .string()
  .regex(/^[1-9]\d*$/)
  .transform(Number)
  .transform((count, context) => {
    const dateRange = insightsDateRangeSchema.safeParse(lastDaysOf(count));
    if (!dateRange.success) {
      context.addIssue({
        code: "custom",
        message: `The last ${count} days are out of range`,
        continue: false,
      });
      return z.NEVER;
    }
    return { count, dateRange: dateRange.data };
  });

const operandOf = (possibleValues: PossibleValues): FieldOperand => {
  switch (possibleValues.kind) {
    case "enum": {
      return { kind: "choices", options: possibleValues.values };
    }
    case "free-text":
    case "date": {
      return { kind: possibleValues.kind };
    }
    case "number": {
      return { kind: "number", format: possibleValues.format };
    }
    default: {
      return possibleValues satisfies never;
    }
  }
};

const vocabularyOf = (member: Member): OperatorVocabulary => {
  switch (member.kind) {
    case "measure":
    case "video-length": {
      return "number";
    }
    case "time": {
      return "time";
    }
    case "creative-tag": {
      return "tag";
    }
    case "listed":
    case "queried":
    case "free-text": {
      return "plain";
    }
    default: {
      return member satisfies never;
    }
  }
};

export const toFilterFolders = (
  folders: readonly FilterableFolder[],
): readonly FilterFolder[] =>
  folders.map((folder) => ({
    name: folder.name,
    fields: folder.members.map((filterable) => ({
      label: filterable.member.shortTitle,
      initial: emptyConditionOf({
        member: filterable.member.name,
        operand: operandOf(filterable.possibleValues),
      }),
      role: filterable.member.kind === "measure" ? "aggregate" : "row",
    })),
  }));

export const operatorOptions = (
  member: Member,
): readonly OperatorChoiceOption[] =>
  operatorChoiceOptions({
    vocabulary: vocabularyOf(member),
    operators: member.operators,
  });

export const toOperandSpec = ({
  leaf,
  filterable,
}: {
  leaf: DraftLeaf;
  filterable: FilterableMember;
}): OperandSpec =>
  operandSpecOf({ leaf, operand: operandOf(filterable.possibleValues) });

const lastDaysText = (days: string): string => {
  const count = daysSchema.safeParse(days);
  if (!count.success) {
    return "";
  }
  const { from, to } = daysOf(count.data.dateRange);
  return `${pluralize("day", count.data.count, true)} (${formatDateRangeLabel(from, to)})`;
};

export const pillSummary = ({
  leaf,
  filterable,
}: {
  leaf: DraftLeaf;
  filterable: FilterableMember;
}): PillSummary =>
  leafSummary({
    leaf,
    operand: operandOf(filterable.possibleValues),
    vocabulary: vocabularyOf(filterable.member),
    daysText: lastDaysText,
  });

const leafFilter = (leaf: DraftLeaf): Found<Filter> => {
  const { member } = leaf;
  switch (leaf.operator) {
    case "set":
    case "notSet": {
      return found({ member, operator: leaf.operator });
    }
    default: {
      const days = relativeDaysOf(leaf);
      switch (days.kind) {
        case "relative": {
          const count = daysSchema.safeParse(days.days);
          return count.success
            ? found({
                member,
                operator: "inDateRange",
                values: [lastDaysOf(count.data.count)],
              })
            : NOT_FOUND;
        }
        case "not-relative": {
          return found({
            member,
            operator: leaf.operator,
            values: leaf.values,
          });
        }
        default: {
          return days satisfies never;
        }
      }
    }
  }
};

type Applied = { view: ViewMeta };

const appliedLeaf = ({
  leaf,
  view,
}: Applied & { leaf: DraftLeaf }): Found<Filter> => {
  const filter = leafFilter(leaf);
  if (filter.kind === "not-found") {
    return filter;
  }
  const parsed = view.filter.safeParse(filter.value);
  return parsed.success ? found(parsed.data) : NOT_FOUND;
};

export const leafApplies = ({
  leaf,
  ...applied
}: Applied & { leaf: DraftLeaf }): boolean =>
  appliedLeaf({ leaf: trimmedLeaf(leaf), ...applied }).kind === "found";

const appliedGroup = ({
  group,
  ...applied
}: Applied & { group: DraftGroup }): Found<Filter> => {
  const filters = childrenOf(group).flatMap((child): Filter[] => {
    const filter = isDraftGroup(child)
      ? appliedGroup({ group: child, ...applied })
      : appliedLeaf({ leaf: child, ...applied });
    return filter.kind === "found" ? [filter.value] : [];
  });
  if (filters.length === 0) {
    return NOT_FOUND;
  }
  return found(logicOf(group) === "and" ? { and: filters } : { or: filters });
};

function compileAdvanced({
  group,
  ...applied
}: Applied & { group: DraftGroup }): CompiledGroup {
  const filter = appliedGroup({ group, ...applied });
  if (filter.kind === "not-found") {
    return err({
      message:
        "No rule in this group applies to this view. Add a condition that uses this view's fields.",
    });
  }
  const parsed = applied.view.filter.safeParse(filter.value);
  return parsed.success
    ? ok(parsed.data)
    : err({ message: rejectionText(toRejection(parsed.error.issues)) });
}

const toRejection = (
  issues: readonly z.core.$ZodIssue[],
): AdvancedRejection => {
  if (
    issues.some(({ message }) => message === "mixes-row-and-aggregate-filters")
  ) {
    return { failure: "mixes-metrics-and-attributes" };
  }
  return {
    failure: "not-runnable",
    reason: issues.map(({ message }) => message).join(", "),
  };
};

function rejectionText(rejection: AdvancedRejection): string {
  switch (rejection.failure) {
    case "mixes-metrics-and-attributes": {
      return "A group can't mix metrics with ad attributes";
    }
    case "not-runnable": {
      return `This group can't run (${rejection.reason})`;
    }
    default: {
      return rejection satisfies never;
    }
  }
}

export const toViewGroupCheck = ({ view }: Applied): CheckGroup =>
  toCheckGroup({
    applies: (leaf) => leafApplies({ leaf, view }),
    compile: (group) => compileAdvanced({ group, view }),
  });

type AdvancedRun =
  | { kind: "no-advanced-group" }
  | { kind: "runs"; filter: Filter }
  | { kind: "not-runnable" };

// A meta refetch can drop a member the applied group was checked against.
const advancedRunOf = ({
  root,
  view,
}: {
  root: DraftRoot;
  view: ViewMeta;
}): AdvancedRun => {
  if (root.advanced === "no-advanced-group") {
    return { kind: "no-advanced-group" };
  }
  const parsed = view.filter.safeParse(root.advanced.filter);
  return parsed.success
    ? { kind: "runs", filter: parsed.data }
    : { kind: "not-runnable" };
};

export function toPageFilters({
  root,
  ...applied
}: Applied & { root: DraftRoot }): readonly Filter[] {
  const leaves = root.conditions.flatMap((leaf) => {
    const filter = appliedLeaf({ leaf, ...applied });
    return filter.kind === "found" ? [filter.value] : [];
  });
  const advanced = advancedRunOf({ root, view: applied.view });
  return advanced.kind === "runs" ? [...leaves, advanced.filter] : leaves;
}

// Meta names each field once across views by its alias, so a member's counterpart is the other view's member under the same alias.
export const memberCounterparts = ({
  from,
  to,
}: {
  from: ViewMeta;
  to: ViewMeta;
}): ReadonlyMap<string, string> => {
  const kinds = new Map(
    [from, to].flatMap((view) =>
      view.folders
        .flatMap((folder) => [...folder.members])
        .map((member): [string, Member["kind"]] => [member.name, member.kind]),
    ),
  );
  const byAlias = new Map(
    [...to.aliases].map(([member, alias]): [string, string] => [alias, member]),
  );
  return new Map(
    [...from.aliases].flatMap(([member, alias]): [string, string][] => {
      const target = byAlias.get(alias);
      const kind = kinds.get(member);
      return target !== undefined &&
        kind !== undefined &&
        kinds.get(target) === kind
        ? [[member, target]]
        : [];
    }),
  );
};

// An or that loses a disjunct would narrow, so it is dropped whole, as a retargeted draft group is.
const retargetedFilter = ({
  filter,
  members,
}: {
  filter: Filter;
  members: ReadonlyMap<string, string>;
}): readonly Filter[] => {
  if ("and" in filter) {
    const kept = filter.and.flatMap((child) =>
      retargetedFilter({ filter: child, members }),
    );
    return kept.length === 0 ? [] : [{ and: kept }];
  }
  if ("or" in filter) {
    const kept = filter.or.flatMap((child) =>
      retargetedFilter({ filter: child, members }),
    );
    return kept.length === filter.or.length ? [{ or: kept }] : [];
  }
  const leaf = leafMemberOf(filter);
  if (leaf.kind === "memberless") {
    return [];
  }
  const target = members.get(leaf.name);
  return target === undefined ? [] : [{ ...filter, member: target }];
};

export const toRetargetedFilters = ({
  filters,
  from,
  to,
}: {
  filters: readonly unknown[];
  from: ViewMeta;
  to: ViewMeta;
}): readonly Filter[] => {
  const members = memberCounterparts({ from, to });
  return filters.flatMap((raw) => {
    const parsed = from.filter.safeParse(raw);
    return parsed.success
      ? retargetedFilter({ filter: parsed.data, members }).flatMap((moved) => {
          const runnable = to.filter.safeParse(moved);
          return runnable.success ? [runnable.data] : [];
        })
      : [];
  });
};

const shownRootOf = ({
  draft,
  view,
}: {
  draft: FilterBarDraft;
  view: ViewMeta;
}): DraftRoot =>
  rootOf({
    draft,
    checked: checkedAdvancedOf({
      advanced: draft.advanced,
      checkGroup: toViewGroupCheck({ view }),
    }),
  });

/** The filters a shown draft runs as, against the view it was drafted on. */
export const shownFiltersOf = ({
  draft,
  view,
}: {
  draft: FilterBarDraft;
  view: ViewMeta;
}): readonly Filter[] =>
  toPageFilters({ root: shownRootOf({ draft, view }), view });

export const advancedRunsOn = ({
  draft,
  view,
}: {
  draft: FilterBarDraft;
  view: ViewMeta;
}): boolean =>
  advancedRunOf({ root: shownRootOf({ draft, view }), view }).kind !==
  "not-runnable";
